import { useFooterToggle } from '@hooks/footerDashboardToogle';
import { useSchemeDetails } from '@services/hooks/scheme';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { ToastType, useTosterStore } from '@store/toster';
import { useSiderToggleMenu } from '@store/useSiderToggleMenu';
import { useComponentLevelAccessCheck } from '../../../authGuard/hooks/useComponentLevelAccessCheck';
import { IFooterTabType } from '.';
import { useSiteAllDetailsStore } from '@store/siteAllDetailsStore';
import { useEndUserStore } from '@store/enduser';
import { useAddNewSiteStore } from '@store/lms/addNewSiteStore';
import { useIsImpersonationLoginUser } from '../../../authGuard/hooks/useIsImpersonationLoginUser';

export const Footer = (): ReactElement => {
	const { pathname } = useLocation();
	const { toggleTab } = useFooterToggle();
	const { refetch } = useSchemeDetails();
	const { setToaster } = useTosterStore();
	const { setFullPageLoader } = useSiderToggleMenu();
	const navigate = useNavigate();
	const { clearSiteDetails } = useSiteAllDetailsStore();
	const {
		setIsEditing,
		setActiveNavTab,
		setIsFormEdit,
		setFormId,
		setFormRevalidate,
		setIsConvertingUpcomingToActive,
	} = useEndUserStore();
	const checkMemberModulePermission = useComponentLevelAccessCheck('/end-users', true);
	const checkSiteSummaryModulePermission = useComponentLevelAccessCheck('/site-summary', true);
	const checkRBDMSiteSummaryModulePermission = useComponentLevelAccessCheck('/rbdm/site-summary', true);
	const checkSchemeModulePermission = useComponentLevelAccessCheck('/scheme', true);
	const checkSitesModulePermission = useComponentLevelAccessCheck('/lms/sites', true);
	const checkMapviewModulePermission = useComponentLevelAccessCheck('/lms/mapview', true);
	const checkRBDMMemberModulePermission = useComponentLevelAccessCheck('rbdm-tabs');
	const { resetAddNewSiteValues } = useAddNewSiteStore();
	const isImpersonationLoginUser = useIsImpersonationLoginUser();

	useEffect(() => {
		toggleTab();
		window.addEventListener('resize', toggleTab);
		return (): void => window.removeEventListener('resize', toggleTab);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, toggleTab]);

	const isActivePath = useCallback(
		(targets: string | string[]): boolean => {
			if (Array.isArray(targets)) {
				return targets.some((path) =>
					path.endsWith('*') ? pathname.startsWith(path.replace('*', '')) : pathname === path
				);
			}
			return targets.endsWith('*') ? pathname.startsWith(targets.replace('*', '')) : pathname === targets;
		},
		[pathname]
	);

	const navigateNewSite = (): void => {
		if (!isImpersonationLoginUser) {
			clearSiteDetails();
			setIsEditing(false);
			setFormId(null);
			setIsFormEdit(false);
			setActiveNavTab([0]);
			setFormRevalidate(false);
			setIsConvertingUpcomingToActive?.(false);
			resetAddNewSiteValues();
			navigate('/lms/add-new-site/site-information');
		}
	};

	const handleScheme = useCallback((): void => {
		setFullPageLoader(true);
		refetch?.()
			.then((result) => {
				setFullPageLoader(false);
				if (!result.data?.length) {
					setToaster(true, ToastType.error, 'No schemes Found!');
				} else {
					result.data.forEach((item) => {
						window.open(item.scheme_url, '_blank')?.focus();
					});
				}
			})
			.catch(() => {
				setFullPageLoader(false);
				setToaster(true, ToastType.error, 'No schemes Found!');
			});
	}, [refetch, setFullPageLoader, setToaster]);

	const tabs: IFooterTabType[] = useMemo(() => {
		const baseTabs: (IFooterTabType | false)[] = [
			{
				key: 'dashboard',
				path: '/',
				label: 'Dashboard',
				icon: 'icon-home',
				active: isActivePath('/'),
				isButton: false,
			},
			checkMemberModulePermission &&
				checkRBDMMemberModulePermission && {
					key: 'end-users',
					path: '/end-users',
					label: 'End Users',
					icon: 'icon-user',
					iconFilled: 'icon-user-filled',
					active: isActivePath('/end-users*'),
					isButton: false,
				},
			!checkRBDMMemberModulePermission && {
				key: 'end-users',
				path: '/rbdm/end-users/all',
				label: 'End Users',
				icon: 'icon-user',
				iconFilled: 'icon-user-filled',
				active: isActivePath('/rbdm/end-users/all*'),
				isButton: false,
			},
			checkSiteSummaryModulePermission &&
				!checkRBDMSiteSummaryModulePermission && {
					key: 'site-summary',
					path: '/site-summary',
					label: 'Site Leads',
					icon: 'icon-tool',
					iconFilled: 'icon-tool-filled',
					active: isActivePath(['/site-summary', '/site-summary/site-specialist-sites', '/site-summary/new-leads']),
					isButton: false,
				},
			checkRBDMSiteSummaryModulePermission && {
				key: 'site-summary',
				path: '/rbdm/site-summary',
				label: 'Site Leads',
				icon: 'icon-tool',
				iconFilled: 'icon-tool-filled',
				active: isActivePath(['/rbdm/site-summary']),
				isButton: false,
			},
			checkSchemeModulePermission && {
				key: 'scheme',
				path: '/scheme',
				label: 'Scheme',
				icon: 'icon-star',
				iconFilled: 'icon-star-filled',
				active: isActivePath('/scheme'),
				isButton: true,
				onClick: handleScheme,
			},
			checkSitesModulePermission && {
				key: 'lms-sites',
				path: '/lms/sites',
				label: 'Sites',
				icon: 'icon-tool',
				iconFilled: 'icon-tool-filled',
				active: isActivePath('/lms/sites*'),
				isButton: false,
			},
			checkMapviewModulePermission && {
				key: 'lms-mapview',
				path: '/lms/mapview',
				label: 'Map View',
				icon: 'icon-folded-paper',
				iconFilled: 'icon-folded-paper',
				active: isActivePath('/lms/mapview'),
				isButton: false,
			},
		];

		return baseTabs.filter(Boolean) as IFooterTabType[];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isActivePath,
		checkMemberModulePermission,
		checkSiteSummaryModulePermission,
		checkSchemeModulePermission,
		handleScheme,
		checkSitesModulePermission,
		checkMapviewModulePermission,
	]);

	const shouldShowIndicator = useMemo(() => {
		const pathsWithIndicator = [
			'/',
			'/site-summary',
			'/scheme',
			'/lms/sites',
			'/lms/mapview',
			'/rbdm/end-users/all',
			'/rbdm/site-summary',
		];
		return pathsWithIndicator.includes(pathname) || pathname.startsWith('/end-users');
	}, [pathname]);

	return (
		<footer>
			<div
				className={`container	${checkSitesModulePermission && checkMapviewModulePermission ? 'lms-menu-container' : ''}`}
			>
				<ul className={`mobile-tab ${checkSitesModulePermission && checkMapviewModulePermission ? 'lms-menu' : ''}`}>
					{tabs.map((tab) => (
						<li key={tab.key} className={`bottombar ${tab.active ? 'active' : ''}`}>
							{tab.isButton ? (
								<button onClick={tab.onClick} aria-label={tab.label}>
									<span className={`${tab.active ? tab.iconFilled : tab.icon} tab-icon`} />
									<span className="footer-smalltext">{tab.label}</span>
									{checkSitesModulePermission && checkMapviewModulePermission && <span className="indicator"></span>}
								</button>
							) : (
								<NavLink to={tab.path} aria-label={tab.label}>
									<span className={`${tab.active ? tab.iconFilled ?? tab.icon : tab.icon} tab-icon`} />
									<span className="footer-smalltext">{tab.label}</span>
									{checkSitesModulePermission && checkMapviewModulePermission && <span className="indicator"></span>}
								</NavLink>
							)}
						</li>
					))}
					{checkSitesModulePermission && checkMapviewModulePermission && (
						<li>
							<div
								className="add-site-btn btn btn-primary"
								aria-label="Add New Site"
								tabIndex={0}
								role="button"
								onClick={navigateNewSite}
							>
								Add New Site <span className="icon-plus-circle"></span>
							</div>
						</li>
					)}

					{shouldShowIndicator && <li className="bottombar-indicator"></li>}
				</ul>
			</div>
		</footer>
	);
};
